import React, { useState } from "react";
import { BtmReturnInspectionProduct, BtmReturnInspectionStatus } from "../model/btm-return-inspection-product";
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, Table, TableBody, TableCell, TableHead, TableRow, TextField, Typography } from "@mui/material";
import { Close, ExpandLess, ExpandMore } from "@mui/icons-material";
import { BtmRequestReportedProductsInspection } from "./btm-request-reported-products-inspection";
import { BtmRequestOrderedProductsInspection } from "./btm-request-ordered-products-inspection";
import { BtmRequestExtraProductsInspection } from "./btm-request-extra-products-inspection";
import { notificationModel } from "../../../../common/component/notifications/notification-model";
import { BtmReturnRequest } from "../model/btm-return-request";
import { IUploadedFileDto } from "../../../../common/model/file-exchange-dto";
import { t } from "i18next";

import "./style.scss";

interface IBtmRequestCustomInspectionDialogProps {
    request: BtmReturnRequest;
    onCompleted: (request: BtmReturnInspectionProduct[]) => Promise<void>;
    onClose: () => void;
}

export const BtmRequestCustomInspectionDialog = (props: IBtmRequestCustomInspectionDialogProps) => {
    const [requestedProductsInspection, setRequestedProductsInspection] = useState<BtmReturnInspectionProduct[]>(
        props.request.inspectionResult.length > 0 ?
            props.request.inspectionResult :
            props.request.declaredProducts.flatMap(BtmReturnInspectionProduct.fromReportedProduct));
    const [orderedProductsInspection, setOrderedProductsInspection] = useState<BtmReturnInspectionProduct[]>([]);
    const [extraProductsInspection, setExtraProductsInspection] = useState<BtmReturnInspectionProduct[]>([]);

    const [isExtraOrderedProductsExpanded, setIsExtraOrderedProductsExpanded] = useState(false);
    const [isExtraUnorderedProductsExpanded, setIsExtraUnorderedProductsExpanded] = useState(false);
    const [isCommentsExpanded, setIsCommentsExpanded] = useState(false);
    const [commentTextValue, setCommentTextValue] = useState("");

    const handleRequestedProductInspected = (index: number, inspectionResult: BtmReturnInspectionStatus,
                                             comment?: string, files?: IUploadedFileDto[]) => {
        setRequestedProductsInspection(prev => prev.map((e, i) =>
            i === index ? e.inspect(inspectionResult, comment, files) : e));
    };

    const handleExtraProductAdd = (inspectionLines: BtmReturnInspectionProduct[]) => {
        setExtraProductsInspection(prev => [ ...prev, ...inspectionLines ]);
    };

    const handleExtraProductDelete = (lineIndex: number) => {
        setExtraProductsInspection(prev => prev.filter((_, index) => index !== lineIndex));
    };

    const handleExtraProductInspected = (index: number, inspectionResult: BtmReturnInspectionStatus, comment?: string, files?: IUploadedFileDto[]) => {
        setExtraProductsInspection(prev => prev.map((e, i) =>
            i === index ? e.inspect(inspectionResult, comment, files) : e));
    };

    const handleInspection = async () => {
        try {
            await props.onCompleted([ ...requestedProductsInspection, ...orderedProductsInspection, ...extraProductsInspection ]);

            notificationModel.addSuccessMessage("Return request has been saved successfully.");
        } catch (error) {
            notificationModel.addErrorMessage("An error occurred while updating the return request. Please try again.");
        }
    };

    return <Dialog open={true} fullWidth={true} maxWidth={"xl"} onClose={props.onClose}>
        <DialogTitle className={"btm-request-accept__title"}>
            {t("components.blueBrandReturns.page.inspection.title", { declarationId: props.request.declarationId })}
            <div className={"btm-request-accept__close"}>
                <IconButton onClick={props.onClose}>
                    <Close />
                </IconButton>
            </div>
        </DialogTitle>
        <DialogContent className={"btm-request-accept"}>
            <Typography>
                {t("components.blueBrandReturns.page.inspection.description")}
            </Typography>
            <Typography variant={"h2"} className={"btm-request-accept__section-title"} fontSize={"medium"} color={"textPrimary"}>
                {t("components.blueBrandReturns.page.inspection.header.reportedProducts")}
            </Typography>
            <Table className={"btm-request-accept__table"}>
                <TableHead>
                    <TableRow>
                        <TableCell>
                            <Typography className={"btm-request-accept__table-title"}>
                                {t("components.blueBrandReturns.page.inspection.column.productName")}
                            </Typography>
                        </TableCell>
                        <TableCell>
                            <Typography className={"btm-request-accept__table-title"}>
                                {t("components.blueBrandReturns.page.inspection.column.declarationProductId")}
                            </Typography>
                        </TableCell>
                        <TableCell>
                            <Typography className={"btm-request-accept__table-title"}>
                                {t("components.blueBrandReturns.page.inspection.column.quantity")}
                            </Typography>
                        </TableCell>
                        <TableCell>
                            <Typography className={"btm-request-accept__table-title"}>
                                {t("components.blueBrandReturns.page.inspection.column.action")}
                            </Typography>
                        </TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {requestedProductsInspection.map((line, index) =>
                        <BtmRequestReportedProductsInspection key={`${line.barcode}-${index}`} data={line}
                                                              onChange={(status, comment, files) =>
                                                                  handleRequestedProductInspected(index, status, comment, files)} />)}
                </TableBody>
            </Table>
            <Typography variant={"h2"} className={"btm-request-accept__section-title"} fontSize={"medium"} color={"textPrimary"}>
                <IconButton onClick={() => setIsExtraOrderedProductsExpanded(prev => !prev)}
                            disabled={isExtraOrderedProductsExpanded && orderedProductsInspection.length > 0}>
                    {isExtraOrderedProductsExpanded ? <ExpandLess /> : <ExpandMore />}
                </IconButton>
                {t("components.blueBrandReturns.page.inspection.header.unreportedOrderedProducts")}
            </Typography>
            {isExtraOrderedProductsExpanded && <Table className={"btm-request-accept__table"}>
                <TableHead>
                    <TableRow>
                        <TableCell>
                            <Typography className={"btm-request-accept__table-title"}>
                                {t("components.blueBrandReturns.page.inspection.column.productName")}
                            </Typography>
                        </TableCell>
                        <TableCell>
                            <Typography className={"btm-request-accept__table-title"}>
                                {t("components.blueBrandReturns.page.inspection.column.isPresent")}
                            </Typography>
                        </TableCell>
                        <TableCell>
                            <Typography className={"btm-request-accept__table-title"}>
                                {t("components.blueBrandReturns.page.inspection.column.productId")}
                            </Typography>
                        </TableCell>
                        <TableCell>
                            <Typography className={"btm-request-accept__table-title"}>
                                {t("components.blueBrandReturns.page.inspection.column.quantity")}
                            </Typography>
                        </TableCell>
                        <TableCell>
                            <Typography className={"btm-request-accept__table-title"}>
                                {t("components.blueBrandReturns.page.inspection.column.action")}
                            </Typography>
                        </TableCell>
                    </TableRow>
                </TableHead>
                <BtmRequestOrderedProductsInspection detailList={props.request.deliveryOrderContents}
                                                     onPresentProductsListChange={list => setOrderedProductsInspection(list)} />
            </Table>}
            <Typography variant={"h2"} className={"btm-request-accept__section-title"} fontSize={"medium"} color={"textPrimary"}>
                <IconButton onClick={() => setIsExtraUnorderedProductsExpanded(prev => !prev)}
                            disabled={isExtraUnorderedProductsExpanded && extraProductsInspection.length > 0}>
                    {isExtraUnorderedProductsExpanded ? <ExpandLess /> : <ExpandMore />}
                </IconButton>
                {t("components.blueBrandReturns.page.inspection.header.otherProducts")}
            </Typography>
            {isExtraUnorderedProductsExpanded && <Table className={"btm-request-accept__table"}>
                <TableHead>
                    <TableRow>
                        <TableCell>
                            <Typography className={"btm-request-accept__table-title"}>
                                {t("components.blueBrandReturns.page.inspection.column.productName")}
                            </Typography>
                        </TableCell>
                        <TableCell>
                            <Typography className={"btm-request-accept__table-title"}>
                                {t("components.blueBrandReturns.page.inspection.column.barcode")}
                            </Typography>
                        </TableCell>
                        <TableCell>
                            <Typography className={"btm-request-accept__table-title"}>
                                {t("components.blueBrandReturns.page.inspection.column.quantity")}
                            </Typography>
                        </TableCell>
                        <TableCell>
                            <Typography className={"btm-request-accept__table-title"}>
                                {t("components.blueBrandReturns.page.inspection.column.action")}
                            </Typography>
                        </TableCell>
                    </TableRow>
                </TableHead>
                <BtmRequestExtraProductsInspection list={extraProductsInspection}
                                                   onInspected={handleExtraProductInspected}
                                                   onAdd={handleExtraProductAdd}
                                                   onDelete={handleExtraProductDelete} />
            </Table>}
            <Typography variant={"h2"} className={"btm-request-accept__section-title btm-request-accept__section-title--clickable"}
                        fontSize={"medium"} color={"textPrimary"}>
                <IconButton onClick={() => setIsCommentsExpanded(prev => !prev)}
                            disabled={isCommentsExpanded && commentTextValue !== ""}>
                    {isCommentsExpanded ? <ExpandLess /> : <ExpandMore />}
                </IconButton>
                {t("components.blueBrandReturns.page.inspection.header.comments")}
            </Typography>
            {isCommentsExpanded && <div>
                <TextField multiline fullWidth minRows={4} maxRows={8} value={commentTextValue}
                           placeholder={t("components.blueBrandReturns.page.inspection.commentPlaceholder")}
                           onChange={e => setCommentTextValue(e.target.value)} />
            </div>}
        </DialogContent>
        <DialogActions>
            <Button variant={"contained"} color={"inherit"} onClick={props.onClose}>
                {t("components.blueBrandReturns.page.inspection.cancelButton")}
            </Button>
            <Button variant={"contained"} color={"secondary"} onClick={handleInspection}>
                {t("components.blueBrandReturns.page.inspection.saveButton")}
            </Button>
        </DialogActions>
    </Dialog>;
};
